<template>
	<v-container fluid class="pa-0">
		<v-card elevation="0">
			<v-card-title v-if="showTitle" class="d-flex justify-space-between">
				<div>Payment List</div>
				<!-- <div>
          <v-btn
            depressed
            rounded
            color="primary"
            @click="paymentDialog = !paymentDialog"
            ><i class="fas fa-plus mr-1"></i>New Payment</v-btn
          >
        </div> -->
			</v-card-title>
			<v-card-text class="pa-0" style="border-top: 1px solid lightgray">
				<v-data-table
					mobile-breakpoint="0"
					:headers="headers"
					:items="filteredPayments"
					:options.sync="options"
					:loading="tableLoading"
					:hide-default-footer="hideFooter"
					:disable-pagination="hideFooter"
					class="elevation-0">
					<template v-if="!hideFilters" v-slot:header>
						<tr>
							<th
								v-for="(header, idx) in headers"
								:key="`header-${idx}`">
								<v-text-field
									v-if="header.value != 'action'"
									dense
									class="ma-2"
									rounded
									v-model="filter[header.value]"
									placeholder="filter"
									solo-inverted
									hide-details
									flat></v-text-field>
							</th>
						</tr>
					</template>
					<template v-slot:[`item.invoiceId`]="{ item }">
						<a
							@click="
								$router.push({
									name: 'InvoiceUpdate',
									params: { id: item.invoiceId },
								})
							">
							{{ item.invoiceId }}
						</a>
					</template>
					<template v-slot:[`item.amount`]="{ item }">
						<span class="mr-1">
							{{ getCurrencySymbol(item.currencyId) }}
						</span>
						<span>{{ convertToCleanNumber(item.amount) }}</span>
					</template>
					<template v-slot:[`item.credited`]="{ item }">
						<div
							v-for="credit in getCreditImpacts(item)"
							:key="`${item.id}-${credit.currencyId}`"
							:class="{
								'red--text': credit.amount < 0,
								'green--text': credit.amount > 0,
							}">
							{{ formatCurrency(credit.currencyId, credit.amount) }}
						</div>
					</template>
					<template v-slot:[`item.credit`]="{ item }">
						<span class="mr-1">
							{{ getCurrencySymbol(item.currencyId) }}
						</span>
						<span>{{ convertToCleanNumber(item.credit) }}</span>
					</template>
					<template v-slot:[`item.typeId`]="{ item }">
						<v-autocomplete
							:rules="[(v) => !!v || 'Required']"
							:items="paymentTypes"
							item-text="displayName"
							item-value="id"
							dense
							rounded
							outlined
							hide-details
							label="Payment Type"
							v-model="item.typeId"
							required>
						</v-autocomplete>
					</template>

					<template v-slot:[`item.paymentDate`]="{ item }">
						{{ convertToDate(getPaymentDate(item), false) }}
					</template>
					<template v-slot:[`item.accountId`]="{ item }">
						<a
							@click="
								$router
									.push({
										name: 'CustomerUpdate',
										params: { id: item.accountId },
									})
									.catch(() => {})
							">
							{{ getCustomerName(item.accountId) }}
						</a>
					</template>
					<template v-slot:[`item.createdBy`]="{ item }">
						{{ getPaymentUserName(item) }}
					</template>
					<template v-slot:[`item.action`]="{ item }">
						<v-btn
							icon
							color="primary"
							@click="updatePaymentBasic(item)"
							:loading="isUpdatingPayment(item)"
							:disabled="!item.typeId || isUpdating">
							<v-icon small>mdi-content-save</v-icon>
						</v-btn>
						<v-btn
							icon
							:color="canDeletePayments ? 'error' : 'grey'"
							@click="openDeleteDialog(item)"
							:loading="isDeletingPayment(item)"
							:disabled="!canDeletePayments || isDeleteLoading || isUpdating">
							<v-icon small>mdi-delete</v-icon>
						</v-btn>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>
		<div style="overflow-x: auto"></div>
		<v-dialog v-model="deleteDialog" max-width="520">
			<v-card>
				<v-card-title class="d-flex align-center">
					<v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
					Delete payment?
				</v-card-title>
				<v-card-text v-if="toDeletePayment" class="pb-0">
					<div class="delete-summary mb-4">
						<div class="text-caption grey--text text--darken-1">
							Customer
						</div>
						<div class="text-subtitle-1 font-weight-medium">
							{{ deleteCustomerName }}
						</div>
						<div class="text-caption grey--text text--darken-1">
							Payment #{{ toDeletePayment.id }}
						</div>
					</div>
					<v-row dense>
						<v-col
							v-for="credit in deleteCreditRows"
							:key="credit.currencyId"
							cols="12"
							sm="6">
							<div class="credit-metric">
								<div class="credit-label">
									{{ credit.label }}
								</div>
								<div class="credit-line">
									<span>Current</span>
									<strong>
										{{
											formatCurrency(
												credit.currencyId,
												credit.current
											)
										}}
									</strong>
								</div>
								<div class="credit-line">
									<span>Delete Impact</span>
									<strong
										:class="{
											'red--text': credit.impact < 0,
											'green--text': credit.impact > 0,
										}">
										{{
											formatSignedCurrency(
												credit.currencyId,
												credit.impact
											)
										}}
									</strong>
								</div>
								<div class="credit-line">
									<span>After Delete</span>
									<strong
										:class="{
											'red--text': credit.projected < 0,
										}">
										{{
											formatCurrency(
												credit.currencyId,
												credit.projected
											)
										}}
									</strong>
								</div>
							</div>
						</v-col>
					</v-row>
					<v-alert
						class="mt-4 mb-0"
						dense
						text
						type="warning">
						{{ deleteConfirmationMessage }}
					</v-alert>
					<v-alert
						v-if="hasNegativeProjectedCredit"
						class="mt-3 mb-0"
						dense
						text
						type="error">
						This customer does not have enough credit for this
						delete. If you continue, the system should reject it.
					</v-alert>
				</v-card-text>
				<v-card-actions class="px-6 pb-5 pt-4">
					<v-spacer></v-spacer>
					<v-btn
						text
						rounded
						:disabled="isDeleteLoading"
						@click="closeDeleteDialog">
						Cancel
					</v-btn>
					<v-btn
						color="error"
						depressed
						rounded
						:loading="isDeleteLoading"
						@click="deletePayment">
						<v-icon small class="mr-1">mdi-delete</v-icon>
						Delete
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!-- <payment-dialog v-if="paymentDialog" v-model="paymentDialog">
    </payment-dialog> -->
	</v-container>
</template>
<script>
import eventBus from "@/js/event-bus.js";
import backendService from "@/services/backend-service";
import generalMixin from "@/js/general-mixin";
import Decimal from "decimal.js";
export default {
	name: "PaymentList",
	mixins: [generalMixin],
	props: {
		value: {
			type: Array,
			default: () => {
				return [];
			},
		},
		customHeaders: {
			type: Array,
			default: () => {
				return [];
			},
		},
		loading: {
			type: Boolean,
			default: () => {
				return false;
			},
		},
		showTitle: {
			type: Boolean,
			default: () => {
				return true;
			},
		},
		hideFilters: {
			type: Boolean,
			default: () => {
				return false;
			},
		},
		hideFooter: {
			type: Boolean,
			default: () => {
				return false;
			},
		},
	},
	mounted: function () {
		eventBus.$on("generateCsv", this.generateCsv);
	},
	data() {
		return {
			defaultHeaders: [
				{
					text: "Id",
					value: "id",
					align: "end",
					width: 100,
				},
				{
					text: "Invoice Id",
					value: "invoiceId",
					align: "end",
					width: 100,
				},
				{ text: "Amount", value: "amount", align: "end", width: 100 },
				// { text: "Used Credit", value: "credit", align: "end", width: 100 },
				{
					text: "Credited",
					value: "credited",
					align: "end",
					width: 100,
				},
				{ text: "Payment Type", value: "typeId", width: 190 },
				{
					text: "Payment Date",
					value: "paymentDate",
					width: 150,
				},
				{ text: "Customer", value: "accountId", width: 150 },
				{ text: "Description", value: "description", width: 200 },
				{ text: "Created By", value: "createdBy", width: 150 },
				{ text: "Action", value: "action", width: 100, sortable: false },
			],
			deleteDialog: false,
			deleteCustomer: null,
			isDeleteLoading: false,
			isUpdateLoading: -1,
			paymentDialog: false,
			toDeletePayment: null,
		};
	},
	computed: {
		paymentTypes() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["paymentType"]
			) {
				return this.$store.getters["dropdownToOptions"]["paymentType"]
					.options;
			}
			return [];
		},
		headerDict() {
			let headerDict = {};
			this.customHeaders.forEach((e) => {
				headerDict[e] = true;
			});
			return headerDict;
		},
		headers() {
			if (this.customHeaders.length == 0) return this.defaultHeaders;
			return this.defaultHeaders.filter(
				(e) => this.headerDict[e.value]
			);
		},
		payments: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			},
		},
		filteredPayments() {
			let payments = this.payments;
			for (let key in this.filter) {
				let searchStr = this.filter[key].trim().toLowerCase();
				let header = this.headers.find((header) => header.value == key);
				if (header && searchStr && searchStr.length) {
					payments = payments.filter((e) => {
						let str = this.getDataText(header, e);
						return str
							.toString()
							.trim()
							.toLowerCase()
							.includes(this.filter[key].toLowerCase());
					});
				}
			}
			return payments;
		},
		tableLoading() {
			return this.loading || this.isDeleteLoading;
		},
		isUpdating() {
			return this.isUpdateLoading != -1;
		},
		canDeletePayments() {
			return this.isAdmin && this.isRoot;
		},
		deleteCreditRows() {
			if (!this.toDeletePayment || !this.deleteCustomer) return [];
			const rows = [
				{
					currencyId: this.Dollar,
					label: "Dollar Credit",
					current: this.deleteCustomer.dollarCredit,
					credited: this.getCurrencyCredited(
						this.toDeletePayment,
						this.Dollar
					),
				},
				{
					currencyId: this.Dong,
					label: "Dong Credit",
					current: this.deleteCustomer.dongCredit,
					credited: this.getCurrencyCredited(
						this.toDeletePayment,
						this.Dong
					),
				},
			];
			return rows.map((row) => {
				const impact = this.decimal(row.credited).times(-1).toNumber();
				const projected = this.decimal(row.current)
					.plus(impact)
					.toNumber();
				return {
					...row,
					impact,
					projected,
				};
			});
		},
		deleteCustomerName() {
			if (!this.deleteCustomer) return "";
			let name = this.deleteCustomer.name || "";
			if (this.deleteCustomer.phone) name += ` - ${this.deleteCustomer.phone}`;
			return name;
		},
		hasNegativeProjectedCredit() {
			return this.deleteCreditRows.some((e) => e.projected < 0);
		},
		deleteConfirmationMessage() {
			const netCredited = this.deleteCreditRows.reduce(
				(sum, row) => sum.plus(row.credited),
				new Decimal(0)
			);
			if (netCredited.greaterThan(0)) {
				return "This payment created customer credit. Deleting it will remove credit from this customer account.";
			}
			if (netCredited.lessThan(0)) {
				return "This payment used customer credit. Deleting it will restore credit to this customer account.";
			}
			return "This action cannot be undone.";
		},
		filter: {
			get() {
				return this.$store.getters["interactStore/paymentFilter"];
			},
			set(val) {
				this.$store.commit("interactStore/setPaymentFilter", val);
			},
		},
		options: {
			get() {
				return this.$store.getters["interactStore/paymentOptions"];
			},
			set(val) {
				this.$store.commit("interactStore/setPaymentOptions", val);
			},
		},
	},
	methods: {
		decimal(value) {
			return new Decimal(value || 0);
		},
		async openDeleteDialog(payment) {
			this.toDeletePayment = payment;
			const customer = await this.getPaymentCustomer(payment);
			if (!customer) {
				this.toDeletePayment = null;
				return;
			}
			this.deleteCustomer = customer;
			this.deleteDialog = true;
		},
		async getPaymentCustomer(payment) {
			let customer = this.customers.find(
				(e) => e.id == payment.accountId
			);
			if (customer) return customer;
			try {
				const response = await backendService.getCustomerById(
					payment.accountId
				);
				return response.data;
			} catch {
				this.errorSnackbar("Unable to verify customer credit");
				return null;
			}
		},
		formatCurrency(currencyId, value) {
			return `${this.getCurrencySymbol(currencyId)}${this.convertToCleanNumber(
				value,
				currencyId != this.Dong
			)}`;
		},
		formatSignedCurrency(currencyId, value) {
			const amount = this.decimal(value);
			const prefix = amount.greaterThan(0) ? "+" : "";
			return `${prefix}${this.formatCurrency(
				currencyId,
				amount.toNumber()
			)}`;
		},
		closeDeleteDialog() {
			this.deleteDialog = false;
			this.toDeletePayment = null;
			this.deleteCustomer = null;
		},
		isDeletingPayment(payment) {
			return (
				this.isDeleteLoading &&
				this.toDeletePayment &&
				this.toDeletePayment.id == payment.id
			);
		},
		isUpdatingPayment(payment) {
			return this.isUpdateLoading == payment.id;
		},
		updatePaymentBasic(payment) {
			this.isUpdateLoading = payment.id;
			backendService
				.updatePaymentBasic(payment)
				.then(() => {
					this.timer(1000).then(() => {
						this.isUpdateLoading = -1;
						this.successSnackbar("Successfully updated payment");
						this.$emit("update", payment);
					});
				})
				.catch(() => {
					this.timer(1000).then(() => {
						this.isUpdateLoading = -1;
						this.errorSnackbar("Ops! Something is not right");
					});
				});
		},
		async deletePayment() {
			if (!this.toDeletePayment) return;
			this.isDeleteLoading = true;
			const paymentId = this.toDeletePayment.id;
			await this.timer(1000);
			backendService
				.deletePaymentById(paymentId)
				.then(() => {
					this.payments = this.payments.filter(
						(e) => e.id != paymentId
					);
					this.$store.dispatch("getCustomers");
					this.$emit("delete", paymentId);
					this.successSnackbar("Successfully deleted the payment");
				})
				.catch((err) => {
					const message =
						err &&
						err.response &&
						typeof err.response.data == "string"
							? err.response.data
							: "";
					if (message.includes("Insufficient credit")) {
						this.errorSnackbar(
							"Cannot delete this payment because the customer does not have enough available credit"
						);
					} else {
						this.errorSnackbar("Ops! Something is not right");
					}
				})
				.finally(() => {
					this.isDeleteLoading = false;
					this.closeDeleteDialog();
				});
		},
		generateCsv() {
			let headerStr =
				"Id;Invoice Id;Amount;Credited;Payment Type;Payment Date;Customer;Created By";
			let csvContent = "";
			csvContent += headerStr + "\r\n";
			for (let i = 0; i < this.filteredPayments.length; i++) {
				const payment = this.filteredPayments[i];
				let rowStr =
					`${payment.id};${
						payment.invoiceId ?? ""
					};${this.convertToCleanNumber(
						payment.amount
					)};${this.convertToCleanNumber(payment.credited)}` +
					`;${this.getOptionName(
						payment.typeId
					)};${this.convertToDate(
						payment.paymentDate,
						false
					)};${this.getCustomerName(payment.accountId)}` +
					`;${this.getUserName(payment.createdBy)}`;
				csvContent += rowStr + "\r\n";
			}
			let blob = new Blob([csvContent], { type: "text/plain" });
			let url = URL.createObjectURL(blob);
			let link = document.createElement("a");
			link.setAttribute("href", url);
			link.setAttribute("download", `payment-list.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
		getDataText(header, payment) {
			if (header.value == "id") {
				return payment.id ? payment.id.toString() : "";
			} else if (header.value == "invoiceId") {
				return payment.invoiceId ? payment.invoiceId.toString() : "";
			} else if (header.value == "amount") {
				return payment.amount == null ? "" : payment.amount;
			} else if (header.value == "credited") {
				return this.getCredited(payment).toString();
			} else if (header.value == "typeId") {
				return this.getOptionName(payment.typeId);
			} else if (header.value == "accountId") {
				return this.getCustomerName(payment.accountId);
			} else if (header.value == "paymentDate") {
				return this.convertToDate(this.getPaymentDate(payment), false);
			} else if (header.value == "description") {
				return payment.description
					? payment.description.toLowerCase()
					: "";
			} else if (header.value == "createdBy") {
				return this.getPaymentUserName(payment);
			} else {
				return "";
			}
		},
		getPaymentUserName(payment) {
			if (payment.createdBy) return this.getUserName(payment.createdBy);
			if (payment.collector) {
				return `${payment.collector.first} ${payment.collector.last}`;
			}
			return "";
		},
		getCredited(payment) {
			if (
				payment.dongCredited != null ||
				payment.dollarCredited != null
			) {
				return this.decimal(payment.dongCredited)
					.plus(payment.dollarCredited)
					.toNumber();
			}
			if (payment.credited != null) return payment.credited;
			if (payment.credit != null) return this.decimal(payment.credit).times(-1).toNumber();
			return 0;
		},
		getCurrencyCredited(payment, currencyId) {
			if (!payment) return 0;
			if (currencyId == this.Dong && payment.dongCredited != null) {
				return payment.dongCredited;
			}
			if (
				currencyId == this.Dollar &&
				payment.dollarCredited != null
			) {
				return payment.dollarCredited;
			}
			if (payment.currencyId == currencyId) return this.getCredited(payment);
			return 0;
		},
		getCreditImpacts(payment) {
			const impacts = [
				{
					currencyId: this.Dollar,
					amount: this.getCurrencyCredited(payment, this.Dollar),
				},
				{
					currencyId: this.Dong,
					amount: this.getCurrencyCredited(payment, this.Dong),
				},
			].filter((e) => !this.decimal(e.amount).equals(0));

			if (impacts.length > 0) return impacts;
			return [{ currencyId: payment.currencyId, amount: 0 }];
		},
		getPaymentDate(payment) {
			return payment.paymentDate || payment.dateCreated;
		},
	},
	destroyed: function () {
		eventBus.$off("generateCsv");
	},
};
</script>
<style scoped>
.delete-summary {
	background: #f5f7fa;
	border: 1px solid #e4e8ee;
	border-radius: 8px;
	padding: 14px 16px;
}
.credit-metric {
	border: 1px solid #e4e8ee;
	border-radius: 8px;
	min-height: 82px;
	padding: 12px;
}
.credit-label {
	color: #667085;
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
}
.credit-value {
	color: #1f2937;
	font-size: 1.05rem;
	font-weight: 700;
	margin-top: 8px;
	word-break: break-word;
}
</style>
